import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CalendlyPage } from "../calendly-api";

/**
 * Forward/back paging over a Calendly cursor endpoint. Calendly only gives a
 * `next_page_token` (no totals / random access), so we keep a stack of the
 * tokens we've visited: the current page is the last entry, "next" pushes the
 * returned token, "prev" pops.
 */
export function useCursorPages<T>(
  baseKey: unknown[],
  fetchPage: (pageToken?: string) => Promise<CalendlyPage<T>>,
) {
  const [stack, setStack] = useState<(string | undefined)[]>([undefined]);
  const currentToken = stack[stack.length - 1];

  const query = useQuery({
    queryKey: [...baseKey, currentToken ?? "first"],
    queryFn: () => fetchPage(currentToken),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const next = () => {
    const token = query.data?.nextPageToken;
    if (token) setStack((s) => [...s, token]);
  };
  const prev = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  return {
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    pageNumber: stack.length, // 1-based
    hasNext: !!query.data?.nextPageToken,
    hasPrev: stack.length > 1,
    next,
    prev,
  };
}
